// Requests use middleware to extend its behaviour
import { isSimpleError, isSimpleErrors, SimpleError, SimpleErrors } from '@common/errors';
import { RequestBag } from './RequestBag.js';
// We still support an older version of simple-encoding, so we stub EncodeMedium here
import * as simpleEncoding from '@common/encoding';
let EncodeMedium;
if ('EncodeMedium' in simpleEncoding) {
    EncodeMedium = simpleEncoding.EncodeMedium;
}
else {
    let StubbedEncodeMedium;
    (function (StubbedEncodeMedium) {
        /**
         * The object will be sent over the network.
         */
        StubbedEncodeMedium["Network"] = "Network";
        /**
         * The object will be stored in the database.
         */
        StubbedEncodeMedium["Database"] = "Database";
    })(StubbedEncodeMedium || (StubbedEncodeMedium = {}));
    EncodeMedium = StubbedEncodeMedium;
}
const { encodeObject, ObjectData, } = simpleEncoding;
export class RequestResult {
    constructor(data, headers = {}, responseVersion) {
        this.data = data;
        this.headers = headers;
        this.responseVersion = responseVersion;
    }
}
export class Request {
    constructor(server, request) {
        var _a, _b, _c, _d, _e, _f;
        /**
         * Enable middleware retries for network/server failures. Decoded API errors
         * can still be retried through allowErrorRetry. Cancellation disables both.
         */
        this.shouldRetry = true;
        /**
         * Even when shouldRetry is false, still allow to retry normal valid errors
         * often needed to refresh a token etc
         */
        this.allowErrorRetry = true;
        /// Request specific middleware
        this.middlewares = [];
        this.errorDecoder = SimpleErrors;
        this.didFailNetwork = false;
        this.XMLHttpRequest = null;
        this.server = server;
        this.method = request.method;
        this.path = request.path;
        this.query = request.query;
        this.body = request.body;
        this.decoder = request.decoder;
        this.headers = (_a = request.headers) !== null && _a !== void 0 ? _a : {};
        this.version = request.version;
        this.timeout = request.timeout;
        this.responseType = (_b = request.responseType) !== null && _b !== void 0 ? _b : '';
        this.responseContentTypeOverride = request.responseContentTypeOverride;
        this.shouldRetry = (_c = request.shouldRetry) !== null && _c !== void 0 ? _c : this.shouldRetry;
        this.allowErrorRetry = (_d = request.allowErrorRetry) !== null && _d !== void 0 ? _d : this.allowErrorRetry;
        this.bag = (_e = request.bag) !== null && _e !== void 0 ? _e : (request.owner ? RequestBag.getOrCreate(request.owner) : undefined);
        this.overrideXMLHttpRequest = request.overrideXMLHttpRequest;
        (_f = this.bag) === null || _f === void 0 ? void 0 : _f.addRequest(this);
    }
    get static() {
        return this.constructor;
    }
    getMiddlewares() {
        return Request.sharedMiddlewares.concat(this.middlewares);
    }
    /**
     * Cancels any pending requests and also disables retries
     */
    cancel() {
        this.shouldRetry = false;
        this.allowErrorRetry = false;
        if (this.XMLHttpRequest) {
            this.XMLHttpRequest.abort();
            this.XMLHttpRequest = null;
        }
        else {
            // Probably a middleware that is running a timeout to retry it later on
            // Immediately call all middlewares to notify them faster of this abort
            // Notify middleware that we stop retrying
            if (!this.didFailNetwork) {
                this.didFailNetwork = true;
                for (const middleware of this.getMiddlewares()) {
                    // Check if one of the middlewares decides to stop
                    if (middleware.onFatalNetworkError) {
                        middleware.onFatalNetworkError(this, new SimpleError({
                            code: 'network_abort',
                            message: 'Network abort',
                        }));
                    }
                }
            }
        }
    }
    /**
     * Cancel all requests with a given owner
     * Shorthand to avoid RequestBag syntax.
     */
    static cancelAll(owner) {
        var _a;
        (_a = RequestBag.get(owner)) === null || _a === void 0 ? void 0 : _a.cancel();
    }
    /** Match the library's network_timeout error code. */
    static isTimeout(e) {
        return !!((isSimpleError(e) || isSimpleErrors(e)) && (e.hasCode('network_timeout')));
    }
    /** Match network failures, timeouts and aborts; use isAbortError to hide only cancellation in the UI. */
    static isNetworkError(e) {
        return !!((isSimpleError(e) || isSimpleErrors(e)) && (e.hasCode('network_error') || e.hasCode('network_timeout') || e.hasCode('network_abort')));
    }
    /** Match cancellation (network_abort), excluding other network failures. */
    static isAbortError(e) {
        return !!((isSimpleError(e) || isSimpleErrors(e)) && (e.hasCode('network_abort')));
    }
    async fetch(data) {
        return new Promise((resolve, reject) => {
            try {
                const request = this.overrideXMLHttpRequest ? (new this.overrideXMLHttpRequest()) : new XMLHttpRequest();
                request.responseType = this.responseType;
                let finished = false;
                request.onreadystatechange = () => {
                    if (finished) {
                        // ignore duplicate events
                        return;
                    }
                    if (request.readyState === 4) {
                        if (request.status === 0) {
                            // should call handleError or handleTimeout
                            return;
                        }
                        finished = true;
                        this.XMLHttpRequest = null;
                        resolve(request);
                    }
                };
                request.ontimeout = () => {
                    if (finished) {
                        // ignore duplicate events
                        return;
                    }
                    finished = true;
                    this.XMLHttpRequest = null;
                    reject(new SimpleError({
                        code: 'network_timeout',
                        message: 'Timeout',
                    }));
                };
                request.onerror = () => {
                    if (finished) {
                        // ignore duplicate events
                        return;
                    }
                    // Your request timed out
                    finished = true;
                    this.XMLHttpRequest = null;
                    reject(new SimpleError({
                        code: 'network_error',
                        message: 'Network error',
                    }));
                };
                request.onabort = () => {
                    if (finished) {
                        // ignore duplicate events
                        return;
                    }
                    finished = true;
                    this.XMLHttpRequest = null;
                    // Disable retries
                    this.shouldRetry = false;
                    reject(new SimpleError({
                        code: 'network_abort',
                        message: 'Network abort',
                    }));
                };
                request.open(data.method, data.url);
                for (const key in data.headers) {
                    if (Object.prototype.hasOwnProperty.call(data.headers, key)) {
                        const value = data.headers[key];
                        request.setRequestHeader(key, value);
                    }
                }
                request.timeout = data.timeout;
                this.XMLHttpRequest = request;
                request.send(data.body);
            }
            catch (e) {
                reject(e);
            }
        });
    }
    async start() {
        // todo: check if already running or not
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        // todo: add query parameters
        for (const middleware of this.getMiddlewares()) {
            if (middleware.onBeforeRequest)
                await middleware.onBeforeRequest(this);
        }
        if (this.didFailNetwork) {
            // In the meantime, the request is canceled before it even started
            // This can happen when the onBeforeRequest did something time intensive (e.g. refresh a token)
            // and in the meantime, the request bag got canceled
            throw new SimpleError({
                code: 'network_abort',
                message: 'Network abort',
            });
        }
        let response;
        let timeout = (_a = this.timeout) !== null && _a !== void 0 ? _a : (this.method === 'GET' ? 30 * 1000 : 35 * 10000);
        try {
            let body;
            // We only support application/json or FormData for now
            if (this.body === undefined) {
                body = undefined;
            }
            else {
                if (this.body instanceof FormData) {
                    body = this.body;
                    let size = 0;
                    for (const [_, value] of this.body.entries()) {
                        if (typeof value === 'string') {
                            size += value.length;
                        }
                        else {
                            size += value.size;
                        }
                    }
                    if (size > 1000 * 1000 * 1000) {
                        // > 1MB upload
                        timeout = Math.max(timeout, 60 * 1000);
                    }
                }
                else {
                    if (!this.headers['Content-Type'] && this.headers['content-type']) {
                        this.headers['Content-Type'] = this.headers['content-type'];
                        delete this.headers['content-type'];
                    }
                    if (this.headers['Content-Type'] && this.headers['Content-Type'].startsWith('application/x-www-form-urlencoded')) {
                        const typeCopy = encodeObject(this.body, {
                            version: (_b = this.version) !== null && _b !== void 0 ? _b : 0,
                            medium: EncodeMedium.Network,
                        });
                        if (typeCopy === null || typeCopy === undefined) {
                            throw new Error('Invalid body, got null/undefined, which is not encodeable to a querystring');
                        }
                        if (Array.isArray(typeCopy)) {
                            throw new Error('Invalid body, got an array which is not encodeable to a querystring');
                        }
                        if (typeof typeCopy === 'object') {
                            body = Object.keys(typeCopy)
                                .filter(k => typeCopy[k] !== undefined)
                                .map((k) => {
                                const v = typeCopy[k];
                                if (typeof v === 'object') {
                                    throw new Error('Invalid body, cannot encode deep objects or arrays in a querystring. Object found at ' + k);
                                }
                                return encodeURIComponent(k) + '=' + encodeURIComponent(v === null ? 'null' : v.toString());
                            })
                                .join('&');
                        }
                    }
                    else {
                        this.headers['Content-Type'] = 'application/json;charset=utf-8';
                        body = JSON.stringify(encodeObject(this.body, {
                            version: (_c = this.version) !== null && _c !== void 0 ? _c : 0,
                            medium: EncodeMedium.Network,
                        }));
                    }
                }
            }
            let queryString = '';
            if (this.query) {
                const query = encodeObject(this.query, {
                    version: (_d = this.version) !== null && _d !== void 0 ? _d : 0,
                    medium: EncodeMedium.Network,
                });
                if (query !== undefined && query !== null) {
                    if (typeof query === 'object' && !Array.isArray(query)) {
                        const params = new URLSearchParams();
                        for (const key in query) {
                            const value = query[key];
                            if (value === null || value === undefined) {
                                // skip
                            }
                            else if (typeof value === 'boolean') {
                                params.set(key, value ? 'true' : 'false');
                            }
                            else if (typeof value === 'number') {
                                if (Number.isFinite(value)) {
                                    params.set(key, value.toString());
                                }
                                else {
                                    throw new SimpleError({
                                        code: 'invalid_query',
                                        message: 'Invalid query parameter with non-integer number value ' + value.toString(),
                                        human: 'Er ging iets mis bij het omvormen van dit verzoek',
                                    });
                                }
                            }
                            else if (typeof value === 'string') {
                                params.set(key, value);
                            }
                            else if (Array.isArray(value)) {
                                for (const v of value) {
                                    if (typeof v === 'boolean') {
                                        params.append(key, v ? 'true' : 'false');
                                    }
                                    else if (typeof v === 'number') {
                                        if (Number.isFinite(v)) {
                                            params.set(key, v.toString());
                                        }
                                        else {
                                            throw new SimpleError({
                                                code: 'invalid_query',
                                                message: 'Invalid query parameter with non-integer number value in array ' + v.toString(),
                                                human: 'Er ging iets mis bij het omvormen van dit verzoek',
                                            });
                                        }
                                    }
                                    else if (typeof v === 'string') {
                                        params.append(key, v);
                                    }
                                    else {
                                        throw new SimpleError({
                                            code: 'invalid_query',
                                            message: 'Invalid query parameter with non-string array value',
                                            human: 'Er ging iets mis bij het omvormen van dit verzoek',
                                        });
                                    }
                                }
                            }
                            else {
                                throw new SimpleError({
                                    code: 'invalid_query',
                                    message: 'Invalid query parameter with non-string value',
                                    human: 'Er ging iets mis bij het omvormen van dit verzoek',
                                });
                            }
                        }
                        const s = params.toString();
                        if (s.length) {
                            queryString = '?' + s;
                        }
                    }
                    else {
                        throw new SimpleError({
                            code: 'invalid_query',
                            message: 'Invalid query parameter of type ' + (typeof query),
                            human: 'Er ging iets mis bij het omvormen van dit verzoek',
                        });
                    }
                }
            }
            if (this.static.verbose) {
                console.log('Starting new request');
                console.log('New request', this.method, this.path, this.body, this.query, this.headers);
            }
            response = await this.fetch({
                url: this.server.host + (this.version !== undefined ? ('/v' + this.version) : '') + this.path + queryString,
                method: this.method,
                headers: this.headers,
                body,
                timeout,
            });
        }
        catch (error) {
            if ((isSimpleError(error) || isSimpleErrors(error)) && error.hasCode('network_timeout')) {
                // Increase next timeout (note: upload will stay 1 minute)
                this.timeout = Math.max(timeout, 45 * 1000);
            }
            // network error is encountered or CORS is misconfigured on the server-side
            // A middleware might decide here to interrupt the callback
            // He might for example fire a timer to retry the request because of a network failure
            // Or it might decide to fetch a new access token because the current one is expired
            // They return a promise with a boolean value indicating that the request should get retried
            if (this.shouldRetry && !this.didFailNetwork) {
                let retry = false;
                for (const middleware of this.getMiddlewares()) {
                    // Check if one of the middlewares decides to stop
                    if (middleware.shouldRetryNetworkError) {
                        retry = retry || (await middleware.shouldRetryNetworkError(this, error));
                    }
                    if (!this.shouldRetry || this.didFailNetwork) {
                        // Stop the loop faster
                        break;
                    }
                }
                // Sometimes, in the meantime, shouldRetry might have become false, so check again
                if (retry && this.shouldRetry && !this.didFailNetwork) {
                    // Retry
                    return await this.start();
                }
            }
            // Notify middleware that we stop retrying
            if (!this.didFailNetwork) {
                // On abort we call this faster if needed (e.g. when middleware is hanging)
                this.didFailNetwork = true;
                for (const middleware of this.getMiddlewares()) {
                    // Check if one of the middlewares decides to stop
                    if (middleware.onFatalNetworkError) {
                        middleware.onFatalNetworkError(this, error);
                    }
                }
            }
            // Failed and not caught
            (_e = this.bag) === null || _e === void 0 ? void 0 : _e.removeRequest(this);
            throw error;
        }
        for (const middleware of this.getMiddlewares()) {
            // Update middleware of recovered network status
            if (middleware.onNetworkResponse) {
                middleware.onNetworkResponse(this, response);
            }
        }
        // Get the version
        let responseVersion;
        for (const header of this.server.versionHeaders) {
            const value = response.getResponseHeader(header);
            if (value) {
                const v = parseInt(value);
                if (!isNaN(v) && isFinite(v)) {
                    responseVersion = v;
                    break;
                }
            }
        }
        if (!responseVersion) {
            // Use the request version by default
            responseVersion = (_f = this.version) !== null && _f !== void 0 ? _f : 0;
        }
        const contentType = (_g = this.responseContentTypeOverride) !== null && _g !== void 0 ? _g : (response.getResponseHeader('Content-Type') || '');
        const mediaType = contentType.split(';')[0].trim().toLowerCase();
        if (response.status < 200 || response.status >= 300) {
            if (mediaType === 'application/json') {
                let err;
                try {
                    let bodyText = await response.response;
                    if (bodyText instanceof Blob) {
                        bodyText = await response.response.text();
                    }
                    const json = typeof bodyText === 'string' ? JSON.parse(bodyText) : bodyText;
                    if (this.errorDecoder) {
                        try {
                            err = this.errorDecoder.decode(new ObjectData(json, {
                                version: responseVersion,
                                medium: EncodeMedium.Network,
                            }));
                            if (this.static.verbose) {
                                console.error(err);
                            }
                        }
                        catch (e) {
                            // Failed to decode
                            if (this.static.verbose) {
                                console.error(json);
                            }
                            throw e;
                        }
                    }
                    else {
                        err = json;
                    }
                }
                catch (e) {
                    return await this.retryOrThrowServerError(response, e);
                }
                // A middleware might decide here to retry instead of passing the error to the caller
                if (isSimpleErrors(err) && (this.shouldRetry || this.allowErrorRetry)) {
                    let retry = false;
                    for (const middleware of this.getMiddlewares()) {
                        // Check if one of the middlewares decides to stop
                        if (middleware.shouldRetryError) {
                            retry = retry || (await middleware.shouldRetryError(this, response, err));
                        }
                    }
                    // Sometimes, in the meantime, shouldRetry might have become false, so check again
                    if (retry && (this.shouldRetry || this.allowErrorRetry)) {
                        // Retry
                        return await this.start();
                    }
                }
                (_h = this.bag) === null || _h === void 0 ? void 0 : _h.removeRequest(this);
                throw err;
            }
            let bodyText = await response.response;
            if (bodyText instanceof Blob) {
                bodyText = await response.response.text();
            }
            // A non 200 status code without json header is always considered as a server error.
            return await this.retryOrThrowServerError(response, new SimpleError({
                code: 'http_status_error',
                message: typeof bodyText === 'string' ? bodyText : response.statusText,
                statusCode: response.status,
            }));
        }
        if (mediaType === 'application/json') {
            // If we have a decoder, we also try to decode the json. Because some servers don't
            // return the correct content type, and we can't fix that
            let json;
            try {
                let bodyText = await response.response;
                if (bodyText instanceof Blob) {
                    bodyText = await response.response.text();
                }
                json = typeof bodyText === 'string' ? JSON.parse(bodyText) : bodyText;
            }
            catch (e) {
                // A 200 status code with invalid JSON is considered a server error
                return await this.retryOrThrowServerError(response, e);
            }
            if (this.decoder) {
                const decoded = (_j = this.decoder) === null || _j === void 0 ? void 0 : _j.decode(new ObjectData(json, {
                    version: responseVersion,
                    medium: EncodeMedium.Network,
                }));
                if (this.static.verbose) {
                    console.info(decoded);
                }
                (_k = this.bag) === null || _k === void 0 ? void 0 : _k.removeRequest(this);
                return new RequestResult(decoded, Request.parseHeaders(response.getAllResponseHeaders()), responseVersion);
            }
            (_l = this.bag) === null || _l === void 0 ? void 0 : _l.removeRequest(this);
            return new RequestResult(json, Request.parseHeaders(response.getAllResponseHeaders()), responseVersion);
        }
        if (this.decoder) {
            // Expected content, but the server didn't respond with content
            if (this.static.verbose) {
                console.error(response.response);
            }
            return await this.retryOrThrowServerError(response, new Error('Missing JSON response from server'));
        }
        (_m = this.bag) === null || _m === void 0 ? void 0 : _m.removeRequest(this);
        return new RequestResult(await response.response, Request.parseHeaders(response.getAllResponseHeaders()), responseVersion);
    }
    static parseHeaders(headers) {
        const result = {};
        for (const line of headers.trim().split(/[\r\n]+/)) {
            const parts = line.split(': ');
            const header = parts.shift();
            if (header) {
                const value = parts.join(': ');
                result[header.toLowerCase()] = value;
            }
        }
        return result;
    }
    async retryOrThrowServerError(response, e) {
        var _a;
        // Invalid json is considered as a server error
        if (this.static.verbose) {
            console.error(e);
        }
        if (this.shouldRetry) {
            // A middleware might decide here to retry instead of passing the error to the caller
            let retry = false;
            for (const middleware of this.getMiddlewares()) {
                // Check if one of the middlewares decides to stop
                if (middleware.shouldRetryServerError) {
                    retry = retry || (await middleware.shouldRetryServerError(this, response, e));
                }
            }
            // Sometimes, in the meantime, shouldRetry might have become false, so check again
            if (retry && this.shouldRetry) {
                // Retry
                return await this.start();
            }
        }
        (_a = this.bag) === null || _a === void 0 ? void 0 : _a.removeRequest(this);
        throw e;
    }
}
/// Shared middlewares that allows dependency injection here
Request.sharedMiddlewares = [];
Request.verbose = false;
//# sourceMappingURL=Request.js.map